/**
 * 6Foot4Honda — 3D Globe Section
 * Dot-matrix globe with land mass points, pulsing city dots,
 * arc connections, grid lines, and full 360 interaction.
 */
(function() {
  'use strict';

  var CFG = {
    radius: 2,
    segments: 64,
    dotSize: 0.035,
    landDotSize: 2.5,
    landDotColor: 0x2299bb,
    cityDotColor: 0xe63946,
    gridColor: 0x335566,
    gridOpacity: 0.12,
    sphereColor: 0x080c10,
    sphereOpacity: 0.35,
    arcColor: 0xe63946,
    rotationSpeed: 0.0008,
    cameraZ: 4.8,
    pulseSpeed: 1.5,
    pulseScale: 2.2,
    dragSensitivity: 0.005,
    initialTiltX: 0.3,
    textureURL: 'https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-dark.jpg'
  };

  // Curated city dots: [name, lat, lon]
  var CITIES = [
    // North America
    ['Los Angeles', 34.05, -118.24],
    ['New York', 40.71, -74.01],
    ['Miami', 25.76, -80.19],
    ['Houston', 29.76, -95.37],
    ['Chicago', 41.88, -87.63],
    ['Toronto', 43.65, -79.38],
    ['Vancouver', 49.28, -123.12],
    ['Dallas', 32.78, -96.80],
    ['Atlanta', 33.75, -84.39],
    ['Denver', 39.74, -104.99],
    ['Phoenix', 33.45, -112.07],
    ['Seattle', 47.61, -122.33],
    ['San Francisco', 37.77, -122.42],
    ['San Diego', 32.72, -117.16],
    ['Las Vegas', 36.17, -115.14],
    ['Portland', 45.52, -122.68],
    ['Austin', 30.27, -97.74],
    ['Nashville', 36.16, -86.78],
    ['Detroit', 42.33, -83.05],
    ['Boston', 42.36, -71.06],
    ['Philadelphia', 39.95, -75.17],
    ['Montreal', 45.50, -73.57],
    ['Calgary', 51.05, -114.07],
    ['Mexico City', 19.43, -99.13],
    ['Honolulu', 21.31, -157.86],
    // South America
    ['Sao Paulo', -23.55, -46.63],
    ['Buenos Aires', -34.60, -58.38],
    ['Rio de Janeiro', -22.91, -43.17],
    ['Bogota', 4.71, -74.07],
    ['Lima', -12.05, -77.04],
    ['Santiago', -33.45, -70.67],
    ['Medellin', 6.25, -75.56],
    // Europe
    ['London', 51.51, -0.13],
    ['Paris', 48.86, 2.35],
    ['Berlin', 52.52, 13.41],
    ['Amsterdam', 52.37, 4.90],
    ['Rome', 41.90, 12.50],
    ['Madrid', 40.42, -3.70],
    ['Stockholm', 59.33, 18.07],
    ['Barcelona', 41.39, 2.17],
    ['Munich', 48.14, 11.58],
    ['Vienna', 48.21, 16.37],
    ['Prague', 50.08, 14.44],
    ['Dublin', 53.35, -6.26],
    ['Lisbon', 38.72, -9.14],
    ['Copenhagen', 55.68, 12.57],
    ['Oslo', 59.91, 10.75],
    ['Helsinki', 60.17, 24.94],
    ['Warsaw', 52.23, 21.01],
    ['Zurich', 47.38, 8.54],
    ['Brussels', 50.85, 4.35],
    ['Milan', 45.46, 9.19],
    ['Manchester', 53.48, -2.24],
    ['Edinburgh', 55.95, -3.19],
    ['Athens', 37.98, 23.73],
    ['Istanbul', 41.01, 28.98],
    ['Bucharest', 44.43, 26.10],
    // Asia
    ['Tokyo', 35.68, 139.69],
    ['Seoul', 37.57, 126.98],
    ['Dubai', 25.20, 55.27],
    ['Mumbai', 19.08, 72.88],
    ['Bangkok', 13.76, 100.50],
    ['Singapore', 1.35, 103.82],
    ['Hong Kong', 22.32, 114.17],
    ['Shanghai', 31.23, 121.47],
    ['Beijing', 39.90, 116.40],
    ['Osaka', 34.69, 135.50],
    ['Delhi', 28.61, 77.21],
    ['Bangalore', 12.97, 77.59],
    ['Jakarta', -6.21, 106.85],
    ['Kuala Lumpur', 3.14, 101.69],
    ['Ho Chi Minh', 10.82, 106.63],
    ['Taipei', 25.03, 121.57],
    ['Manila', 14.60, 120.98],
    ['Riyadh', 24.69, 46.72],
    ['Tel Aviv', 32.09, 34.78],
    ['Doha', 25.29, 51.53],
    // Oceania
    ['Sydney', -33.87, 151.21],
    ['Auckland', -36.85, 174.76],
    ['Melbourne', -37.81, 144.96],
    ['Brisbane', -27.47, 153.03],
    ['Perth', -31.95, 115.86],
    // Africa
    ['Cape Town', -33.93, 18.42],
    ['Lagos', 6.52, 3.38],
    ['Johannesburg', -26.20, 28.04],
    ['Nairobi', -1.29, 36.82],
    ['Cairo', 30.04, 31.24],
    ['Casablanca', 33.57, -7.59],
    ['Accra', 5.56, -0.19],
    ['Addis Ababa', 9.02, 38.75]
  ];

  // Arc connections: [fromIndex, toIndex]
  var ARCS = [
    [0, 32],  // LA → London
    [1, 34],  // NYC → Berlin
    [1, 33],  // NYC → Paris
    [57, 0],  // Tokyo → LA
    [59, 62], // Dubai → Singapore
    [78, 57], // Sydney → Tokyo
    [3, 25],  // Houston → Sao Paulo
    [32, 59], // London → Dubai
    [4, 35],  // Chicago → Amsterdam
    [83, 33], // Cape Town → Paris
    [12, 57], // SF → Tokyo
    [1, 32],  // NYC → London
    [38, 29], // Stockholm → Lima
    [63, 78], // Hong Kong → Sydney
    [5, 43],  // Toronto → Dublin
    [9, 23],  // Denver → Mexico City
    [60, 68], // Mumbai → Jakarta
    [37, 84],  // Madrid → Lagos
    [11, 58], // Seattle → Seoul
    [16, 40]  // Austin → Barcelona
  ];

  // Continent labels: [text, lat, lon, scale]
  var LABELS = [
    ['NORTH AMERICA', 48, -100, 1.0],
    ['SOUTH AMERICA', -18, -58, 0.75],
    ['EUROPE', 55, 18, 0.55],
    ['AFRICA', 5, 22, 0.75],
    ['ASIA', 48, 85, 1.0],
    ['OCEANIA', -28, 140, 0.6]
  ];

  var container = document.getElementById('globeContainer');
  var canvas = document.getElementById('globeCanvas');
  if (!container || !canvas) return;

  var scene, camera, renderer, globeGroup, globeMesh, dotGroup;
  var animationId = null;
  var isVisible = false;
  var hasInitialized = false;
  var statsAnimated = false;
  var isDragging = false;
  var prevMouseX = 0;
  var prevMouseY = 0;
  var targetRotY = 0;
  var targetRotX = CFG.initialTiltX;
  var currentRotY = 0;
  var currentRotX = CFG.initialTiltX;
  var cityDots = [];
  var arcLines = [];
  var clock = null;

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch(e) { return false; }
  }

  if (!hasWebGL()) {
    container.innerHTML = '<div class="globe-fallback">30+ Countries &bull; 250K+ Community &bull; Worldwide</div>';
    return;
  }

  function latLonTo3D(lat, lon, r) {
    var phi = (90 - lat) * (Math.PI / 180);
    var theta = (lon + 180) * (Math.PI / 180);
    return new THREE.Vector3(
      -(r * Math.sin(phi) * Math.cos(theta)),
      r * Math.cos(phi),
      r * Math.sin(phi) * Math.sin(theta)
    );
  }

  // Create canvas text sprite for labels
  function createLabel(text, scale) {
    var cvs = document.createElement('canvas');
    cvs.width = 512;
    cvs.height = 96;
    var ctx = cvs.getContext('2d');
    ctx.font = '600 30px Inter, Arial, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var chars = text.split('');
    var spacing = 4;
    var totalW = 0;
    var widths = [];
    for (var i = 0; i < chars.length; i++) {
      var cw = ctx.measureText(chars[i]).width;
      widths.push(cw);
      totalW += cw + (i < chars.length - 1 ? spacing : 0);
    }
    var x = (512 - totalW) / 2;
    for (var j = 0; j < chars.length; j++) {
      ctx.fillText(chars[j], x + widths[j] / 2, 48);
      x += widths[j] + spacing;
    }
    var tex = new THREE.CanvasTexture(cvs);
    var mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: true, depthWrite: false });
    var sprite = new THREE.Sprite(mat);
    sprite.scale.set(scale * 1.3, scale * 0.25, 1);
    return sprite;
  }

  // Create lat/lon grid lines
  function createGrid() {
    var group = new THREE.Group();
    var r = CFG.radius + 0.002;
    var mat = new THREE.LineBasicMaterial({ color: CFG.gridColor, transparent: true, opacity: CFG.gridOpacity });

    // Longitude (meridians) every 30 deg
    for (var lon = 0; lon < 360; lon += 30) {
      var pts = [];
      for (var lat = -90; lat <= 90; lat += 2) pts.push(latLonTo3D(lat, lon, r));
      group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat));
    }
    // Latitude (parallels) every 30 deg
    for (var la = -60; la <= 60; la += 30) {
      var pts2 = [];
      for (var lo = 0; lo <= 360; lo += 2) pts2.push(latLonTo3D(la, lo, r));
      group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts2), mat));
    }
    // Equator slightly brighter
    var eqPts = [];
    for (var elo = 0; elo <= 360; elo += 2) eqPts.push(latLonTo3D(0, elo, r));
    var eqMat = new THREE.LineBasicMaterial({ color: CFG.gridColor, transparent: true, opacity: CFG.gridOpacity * 1.8 });
    group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(eqPts), eqMat));

    return group;
  }

  // Generate land mass dots from texture (dot-matrix style)
  function generateLandDots(texture) {
    var img = texture.image;
    var cvs = document.createElement('canvas');
    var w = 360, h = 180;
    cvs.width = w;
    cvs.height = h;
    var ctx = cvs.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    var data = ctx.getImageData(0, 0, w, h).data;

    var positions = [];
    var step = 2.8; // degrees between samples

    for (var lat = -85; lat <= 85; lat += step) {
      for (var lon = -180; lon <= 180; lon += step) {
        var px = Math.floor(((lon + 180) / 360) * w);
        var py = Math.floor(((90 - lat) / 180) * h);
        var idx = (py * w + px) * 4;
        var brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;

        if (brightness > 18) {
          // Add some jitter for organic look
          var jLat = lat + (Math.random() - 0.5) * step * 0.4;
          var jLon = lon + (Math.random() - 0.5) * step * 0.4;
          var pos = latLonTo3D(jLat, jLon, CFG.radius + 0.008);
          positions.push(pos.x, pos.y, pos.z);
        }
      }
    }

    var geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    // Create circular dot texture
    var dotCvs = document.createElement('canvas');
    dotCvs.width = 32;
    dotCvs.height = 32;
    var dCtx = dotCvs.getContext('2d');
    var gradient = dCtx.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, 'rgba(34, 153, 187, 0.9)');
    gradient.addColorStop(0.5, 'rgba(34, 153, 187, 0.4)');
    gradient.addColorStop(1, 'rgba(34, 153, 187, 0)');
    dCtx.fillStyle = gradient;
    dCtx.fillRect(0, 0, 32, 32);
    var dotTex = new THREE.CanvasTexture(dotCvs);

    var pointsMat = new THREE.PointsMaterial({
      map: dotTex,
      color: CFG.landDotColor,
      size: CFG.landDotSize,
      transparent: true,
      opacity: 0.7,
      sizeAttenuation: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    var points = new THREE.Points(geo, pointsMat);
    globeMesh.add(points);
  }

  // Create arc between two cities
  function createArc(fromPos, toPos) {
    var mid = fromPos.clone().add(toPos).multiplyScalar(0.5);
    var dist = fromPos.distanceTo(toPos);
    mid.normalize().multiplyScalar(CFG.radius + dist * 0.35);

    var curve = new THREE.QuadraticBezierCurve3(fromPos, mid, toPos);
    var pts = curve.getPoints(60);
    var geo = new THREE.BufferGeometry().setFromPoints(pts);

    var mat = new THREE.LineBasicMaterial({
      color: CFG.arcColor,
      transparent: true,
      opacity: 0.0
    });

    var line = new THREE.Line(geo, mat);
    return { line: line, mat: mat, phase: Math.random() * Math.PI * 2 };
  }

  function initGlobe() {
    var w = container.clientWidth;
    var h = container.clientHeight;

    scene = new THREE.Scene();

    camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.z = CFG.cameraZ;

    renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    // Lighting
    scene.add(new THREE.AmbientLight(0x555555, 1));
    var keyLight = new THREE.DirectionalLight(0xffffff, 0.4);
    keyLight.position.set(5, 3, 5);
    scene.add(keyLight);

    // Globe group for X tilt
    globeGroup = new THREE.Group();
    scene.add(globeGroup);

    // Dark translucent sphere
    var sphereGeo = new THREE.SphereGeometry(CFG.radius, CFG.segments, CFG.segments);
    globeMesh = new THREE.Mesh(sphereGeo, new THREE.MeshBasicMaterial({
      color: CFG.sphereColor,
      transparent: true,
      opacity: CFG.sphereOpacity
    }));
    globeGroup.add(globeMesh);

    // Grid lines
    var grid = createGrid();
    globeMesh.add(grid);

    // Atmosphere glow
    var atmosGeo = new THREE.SphereGeometry(CFG.radius * 1.12, CFG.segments, CFG.segments);
    var atmosMat = new THREE.ShaderMaterial({
      vertexShader: [
        'varying vec3 vNormal;',
        'void main() {',
        '  vNormal = normalize(normalMatrix * normal);',
        '  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);',
        '}'
      ].join('\n'),
      fragmentShader: [
        'varying vec3 vNormal;',
        'void main() {',
        '  float intensity = pow(0.6 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.5);',
        '  gl_FragColor = vec4(0.13, 0.6, 0.73, 0.8) * intensity;',
        '}'
      ].join('\n'),
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true
    });
    scene.add(new THREE.Mesh(atmosGeo, atmosMat));

    // Load earth texture as DATA SOURCE for land dot placement (not visible on sphere)
    var loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    loader.load(CFG.textureURL, function(tex) {
      generateLandDots(tex);
    });

    // City dots (bright, pulsing)
    dotGroup = new THREE.Group();
    globeMesh.add(dotGroup);

    var dotGeo = new THREE.SphereGeometry(CFG.dotSize, 12, 12);
    var ringGeo = new THREE.SphereGeometry(CFG.dotSize * 3, 12, 12);

    for (var i = 0; i < CITIES.length; i++) {
      var city = CITIES[i];
      var pos = latLonTo3D(city[1], city[2], CFG.radius + 0.015);

      var coreMat = new THREE.MeshBasicMaterial({ color: CFG.cityDotColor, transparent: true, opacity: 1.0 });
      var core = new THREE.Mesh(dotGeo, coreMat);
      core.position.copy(pos);
      dotGroup.add(core);

      var ringMat = new THREE.MeshBasicMaterial({ color: CFG.cityDotColor, transparent: true, opacity: 0.3 });
      var ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.copy(pos);
      dotGroup.add(ring);

      cityDots.push({ core: core, ring: ring, phase: (i / CITIES.length) * Math.PI * 2 });
    }

    // Arc connections
    for (var a = 0; a < ARCS.length; a++) {
      var from = CITIES[ARCS[a][0]];
      var to = CITIES[ARCS[a][1]];
      var fromPos = latLonTo3D(from[1], from[2], CFG.radius + 0.015);
      var toPos = latLonTo3D(to[1], to[2], CFG.radius + 0.015);
      var arc = createArc(fromPos, toPos);
      globeMesh.add(arc.line);
      arcLines.push(arc);
    }

    // Continent labels
    for (var k = 0; k < LABELS.length; k++) {
      var lbl = LABELS[k];
      var sprite = createLabel(lbl[0], lbl[3]);
      var lPos = latLonTo3D(lbl[1], lbl[2], CFG.radius + 0.2);
      sprite.position.copy(lPos);
      globeMesh.add(sprite);
    }

    clock = new THREE.Clock();

    // Mouse drag
    canvas.addEventListener('mousedown', function(e) {
      isDragging = true;
      prevMouseX = e.clientX;
      prevMouseY = e.clientY;
      canvas.style.cursor = 'grabbing';
    });
    canvas.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      targetRotY += (e.clientX - prevMouseX) * CFG.dragSensitivity;
      targetRotX -= (e.clientY - prevMouseY) * CFG.dragSensitivity;
      targetRotX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, targetRotX));
      prevMouseX = e.clientX;
      prevMouseY = e.clientY;
    });
    canvas.addEventListener('mouseup', function() { isDragging = false; canvas.style.cursor = 'grab'; });
    canvas.addEventListener('mouseleave', function() { isDragging = false; canvas.style.cursor = 'grab'; });

    // Touch drag
    canvas.addEventListener('touchstart', function(e) {
      if (e.touches.length === 1) {
        isDragging = true;
        prevMouseX = e.touches[0].clientX;
        prevMouseY = e.touches[0].clientY;
      }
    }, { passive: true });
    canvas.addEventListener('touchmove', function(e) {
      if (!isDragging || e.touches.length !== 1) return;
      targetRotY += (e.touches[0].clientX - prevMouseX) * CFG.dragSensitivity;
      targetRotX -= (e.touches[0].clientY - prevMouseY) * CFG.dragSensitivity;
      targetRotX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, targetRotX));
      prevMouseX = e.touches[0].clientX;
      prevMouseY = e.touches[0].clientY;
    }, { passive: true });
    canvas.addEventListener('touchend', function() { isDragging = false; });

    canvas.style.cursor = 'grab';

    window.addEventListener('resize', function() {
      if (!renderer || !camera || !container) return;
      var nw = container.clientWidth;
      var nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    });
  }

  function animate() {
    if (!isVisible) return;
    animationId = requestAnimationFrame(animate);

    var elapsed = clock.getElapsedTime();

    if (!isDragging) targetRotY += CFG.rotationSpeed;
    currentRotY += (targetRotY - currentRotY) * 0.05;
    currentRotX += (targetRotX - currentRotX) * 0.05;
    globeGroup.rotation.x = currentRotX;
    globeMesh.rotation.y = currentRotY;

    // Pulse city dots
    for (var i = 0; i < cityDots.length; i++) {
      var d = cityDots[i];
      var pulse = (Math.sin(elapsed * CFG.pulseSpeed + d.phase) + 1) / 2;
      var scale = 1 + pulse * (CFG.pulseScale - 1);
      d.ring.scale.set(scale, scale, scale);
      d.ring.material.opacity = 0.35 * (1 - pulse * 0.8);
      d.core.material.opacity = 0.8 + pulse * 0.2;
    }

    // Animate arcs (breathing opacity)
    for (var j = 0; j < arcLines.length; j++) {
      var arc = arcLines[j];
      var anim = (Math.sin(elapsed * 0.8 + arc.phase) + 1) / 2;
      arc.mat.opacity = 0.08 + anim * 0.2;
    }

    renderer.render(scene, camera);
  }

  function startAnimation() {
    if (animationId) return;
    isVisible = true;
    if (!clock) return;
    clock.start();
    animate();
  }

  function stopAnimation() {
    isVisible = false;
    if (animationId) { cancelAnimationFrame(animationId); animationId = null; }
    if (clock) clock.stop();
  }

  // Count-up
  function animateCountUp() {
    var els = document.querySelectorAll('.globe-stat__number[data-target]');
    for (var i = 0; i < els.length; i++) {
      (function(el) {
        var target = parseInt(el.getAttribute('data-target'), 10);
        var suffix = el.getAttribute('data-suffix') || '';
        var start = null;
        function step(ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / 2000, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(eased * target) + suffix;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      })(els[i]);
    }
  }

  // Lazy load Three.js
  function loadThreeJS(cb) {
    if (window.THREE) { cb(); return; }
    var s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js';
    s.onload = cb;
    s.onerror = function() {
      container.innerHTML = '<div class="globe-fallback">30+ Countries &bull; 250K+ Community &bull; Worldwide</div>';
    };
    document.head.appendChild(s);
  }

  // IntersectionObserver lifecycle
  var section = document.getElementById('globeSection');
  if (!section) return;

  if ('IntersectionObserver' in window) {
    var preloadObs = new IntersectionObserver(function(entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && !hasInitialized) {
          loadThreeJS(function() {
            initGlobe();
            hasInitialized = true;
            if (isVisible) startAnimation();
          });
          preloadObs.unobserve(entries[i].target);
        }
      }
    }, { rootMargin: '200px 0px' });
    preloadObs.observe(section);

    var visObs = new IntersectionObserver(function(entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          isVisible = true;
          if (hasInitialized) startAnimation();
          if (!statsAnimated) { statsAnimated = true; animateCountUp(); }
        } else {
          stopAnimation();
        }
      }
    }, { threshold: 0.1 });
    visObs.observe(section);
  } else {
    loadThreeJS(function() {
      initGlobe();
      hasInitialized = true;
      isVisible = true;
      startAnimation();
      animateCountUp();
    });
  }
})();
